import { ArrowRight, IdCard, LogIn, School, UsersRound } from "lucide-react";

import { Link } from "../components/Link";
import { Modal } from "../components/Modal";

import { useGuideState } from "./guideStorage";

import type { ReactNode } from "react";

/** Guide card id that "다시 보지 않기" records. */
export const BASIC_FLOW_ID = "basic-flow";

const shownKey = (userId: string) => `canvaschool:basic-flow-shown:${userId}`;

/** Whether the flow already opened in this tab; a new tab or login shows it again. */
const shownThisSession = (userId: string): boolean => {
  try {
    return window.sessionStorage.getItem(shownKey(userId)) === "1";
  } catch {
    return false;
  }
};

export const markBasicFlowShown = (userId: string) => {
  try {
    window.sessionStorage.setItem(shownKey(userId), "1");
  } catch {
    // Without storage the flow may open again on the next page load.
  }
};

/** Cleared on sign-in so every login opens the flow once. */
export const clearBasicFlowShown = (userId: string) => {
  try {
    window.sessionStorage.removeItem(shownKey(userId));
  } catch {
    // Nothing was saved.
  }
};

type FlowStep = {
  who: "선생님" | "학생";
  icon: ReactNode;
  title: string;
  body: string;
  example: ReactNode;
};

const FLOW_STEPS: FlowStep[] = [
  {
    who: "선생님",
    icon: <School size={28} aria-hidden="true" />,
    title: "학급 만들기",
    body: "학급을 만들면 학생 번호마다 아이디가 생겨요.",
    example: (
      <span className="canvaschool-flow-chip">class3-1_1 · class3-1_2 …</span>
    ),
  },
  {
    who: "선생님",
    icon: <IdCard size={28} aria-hidden="true" />,
    title: "아이디 나눠 주기",
    body: "학생 계정 탭에서 학생별 안내문을 인쇄해 아이마다 한 장씩 나눠 줘요. 명단은 선생님 보관용이에요.",
    example: <span className="canvaschool-flow-chip">비밀번호 12345678</span>,
  },
  {
    who: "학생",
    icon: <LogIn size={28} aria-hidden="true" />,
    title: "학생이 로그인",
    body: "학생이 자기 기기에서 crewvas.com에 들어와 받은 아이디로 로그인해요.",
    example: <span className="canvaschool-flow-chip">crewvas.com</span>,
  },
  {
    who: "학생",
    icon: <UsersRound size={28} aria-hidden="true" />,
    title: "캔버스에서 함께",
    body: "선생님이 만든 캔버스를 학생이 열고 그려요. 선생님 화면에도 바로 보여요.",
    example: <span className="canvaschool-flow-chip">실시간으로 함께</span>,
  },
];

/** The four-step picture of how a class is used. */
export const BasicFlowDiagram = () => (
  <ol className="canvaschool-flow" aria-label="크루바스 기본 흐름">
    {FLOW_STEPS.map((step, index) => (
      <li
        key={step.title}
        className={`canvaschool-flow-step canvaschool-flow-step--${
          step.who === "선생님" ? "teacher" : "student"
        }`}
      >
        <span className="canvaschool-flow-who">
          {index + 1} · {step.who}
        </span>
        <span className="canvaschool-flow-icon">{step.icon}</span>
        <strong className="canvaschool-flow-title">{step.title}</strong>
        <span className="canvaschool-flow-body">{step.body}</span>
        {step.example}
        {index < FLOW_STEPS.length - 1 && (
          <ArrowRight
            className="canvaschool-flow-arrow"
            size={20}
            aria-hidden="true"
          />
        )}
      </li>
    ))}
  </ol>
);

/**
 * Opens over a teacher's page once per login until they choose
 * "다시 보지 않기". Many teachers reach a canvas without realising students
 * sign in with their own accounts to join it.
 */
export const BasicFlowModal = ({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}) => {
  const { dismissCard } = useGuideState(userId);
  return (
    <Modal
      title="학생과 함께 쓰는 방법"
      className="canvaschool-modal--flow"
      hideActions
      primaryLabel="확인"
      onPrimary={onClose}
      onCancel={onClose}
    >
      <p className="canvaschool-page-lead">
        캔버스는 선생님 혼자 쓰는 곳이 아니에요. 학생들이 각자 아이디로
        로그인해서 선생님이 만든 캔버스에 들어와 함께 그려요.
      </p>
      <BasicFlowDiagram />
      <p className="canvaschool-hint">
        실제 화면으로 하나씩 보려면{" "}
        <Link to="/manual" onClick={onClose}>
          매뉴얼
        </Link>
        을 열어 보세요.
      </p>
      <div className="canvaschool-modal-actions">
        <button
          type="button"
          className="canvaschool-btn-secondary"
          onClick={() => {
            dismissCard(BASIC_FLOW_ID);
            onClose();
          }}
        >
          다시 보지 않기
        </button>
        <button
          type="button"
          className="canvaschool-btn-primary"
          onClick={onClose}
        >
          확인했어요
        </button>
      </div>
    </Modal>
  );
};

/** Whether the flow should open now, recording that it did. */
export const takeBasicFlowTurn = (
  userId: string,
  dismissed: readonly string[],
): boolean => {
  if (dismissed.includes(BASIC_FLOW_ID) || shownThisSession(userId)) {
    return false;
  }
  markBasicFlowShown(userId);
  return true;
};
